import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap } from "lucide-react";
import { priceToNumber } from "@/lib/apiClient";

const statusStyles = {
  available: "text-gold border-gold/40",
  reserved: "text-ink-dim border-white/15",
  acquired: "text-ink-dim/60 border-white/10 line-through",
};
const statusLabel = { available: "Available", reserved: "Reserved", acquired: "Acquired" };

export default function DomainCard({ domain, index, onInquire, onBuy }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: -300, y: -300 });
  const disabled = domain.status === "acquired";
  const numericPrice = priceToNumber(domain.price);
  const buyable = !disabled && numericPrice != null;

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setPos({ x: -300, y: -300 })}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`domain-card-${domain.name}`}
      className="group relative h-full overflow-hidden rounded-2xl border border-line bg-[var(--panel)] min-h-[260px] md:min-h-[280px] flex flex-col p-6 sm:p-8"
    >
      {/* glitter background */}
      <div
        className="glitter-layer pointer-events-none absolute inset-0 z-0"
        style={{ "--glitter-delay": `${(index % 6) * 0.5}s` }}
      />

      {/* spotlight glow */}
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `radial-gradient(400px circle at ${pos.x}px ${pos.y}px, rgba(212,175,55,0.12), transparent 60%)` }}
      />
      {/* gradient border on hover */}
      <div
        className="pointer-events-none absolute inset-0 z-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(440px circle at ${pos.x}px ${pos.y}px, rgba(212,175,55,0.55), transparent 45%)`,
          WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: "1px",
        }}
      />

      <div className="relative z-10 flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
        <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border ${statusStyles[domain.status]}`}>
          {statusLabel[domain.status]}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-dim">{domain.category}</span>
      </div>

      <div className="relative z-10 mt-6">
        <h3 className="font-mono text-lg sm:text-2xl md:text-3xl text-gold-gradient mb-3 break-all leading-none">
          {domain.name}
        </h3>
        <p className="font-serif italic text-lg text-ink-dim">{domain.tagline}</p>
      </div>

      <div className="relative z-10 mt-auto flex flex-col gap-5 pt-6">
        <div className="min-w-0">
          <div className="overline mb-1 whitespace-nowrap">Guide Price</div>
          <div className="font-mono text-lg text-ink whitespace-nowrap">{domain.price}</div>
        </div>
        <div className="flex flex-wrap items-center gap-3.5">
          <button
            disabled={disabled}
            onClick={() => onInquire(domain)}
            data-testid={`inquire-button-${domain.name}`}
            className={`inquire-btn flex items-center justify-center gap-1.5 rounded-full border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] whitespace-nowrap shrink-0 transition-colors duration-300 ${
              disabled ? "text-ink-dim/40 border-white/5 cursor-not-allowed" : "text-ink border-white/15 hover:border-gold/60"
            }`}
          >
            {disabled ? "Sold" : "Inquire"}
            {!disabled && <ArrowUpRight size={13} />}
          </button>
          {buyable ? (
            <button
              onClick={() => onBuy(domain)}
              data-testid={`buy-button-${domain.name}`}
              className="flex items-center justify-center gap-1.5 rounded-full bg-gold text-bg px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] font-bold whitespace-nowrap shrink-0 hover:shadow-[0_0_22px_rgba(212,175,55,0.5)] transition-shadow duration-300"
            >
              <Zap size={13} /> Buy Now
            </button>
          ) : (
            <span
              aria-hidden="true"
              className="invisible flex items-center gap-1.5 rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] font-bold whitespace-nowrap shrink-0"
            >
              <Zap size={13} /> Buy Now
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
